import { Card, CardContent, Typography, Alert, Divider, Stack } from '@mui/material';

import type { IntakeStatusBaseSteps } from '@shared/patient-intake-status';

import { NoteDisplay, StatusIcon } from '../../../components';
import { CurrentStatusViewer } from './CurrentStatusViewer.tsx';

type BaseStepViewerProps<TBaseField extends keyof IntakeStatusBaseSteps> = {
  baseField: TBaseField;
  step: IntakeStatusBaseSteps[NoInfer<TBaseField>];
};

export const BaseStepViewer = <TBaseField extends keyof IntakeStatusBaseSteps>({
  step,
  baseField,
}: BaseStepViewerProps<TBaseField>) => {
  if (!step) {
    return <Alert severity="warning">Verification step data not found.</Alert>;
  }

  const { status, actionedAt, actionedBy, note } = step.currentStatus;

  if (!status || status === 'NOT_STARTED' || status === 'ERROR') {
    return null;
  }

  return (
    <Card variant="outlined">
      <CardContent sx={{ p: '0.5rem !important' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
          <Typography variant="body1" fontWeight="bold">
            Current Status
          </Typography>
          <StatusIcon status={status} stepIdentifier={baseField} />
        </Stack>

        <Stack direction="column" alignItems="flex-start" justifyContent="space-between" gap={0} mb={1}>
          {!!actionedAt && <Typography variant="body2">Updated on {actionedAt.toDate().toLocaleString()}</Typography>}
          {!!actionedBy && (
            <Typography variant="body2">
              By {actionedBy.name || actionedBy.email} ({actionedBy.userType})
            </Typography>
          )}
        </Stack>

        <NoteDisplay stepId={baseField} noteConfig={note} />

        <Divider sx={{ my: 1, '&:last-child': { display: 'none' } }} />

        <CurrentStatusViewer baseField={baseField} step={step} />
      </CardContent>
    </Card>
  );
};
